import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  // --accent-solid rather than --accent: the fill has to carry a label, and in
  // dark mode --accent is the bright jade that reads as text on a surface, not
  // as a surface under text.
  primary:
    'bg-[var(--accent-solid)] text-[var(--accent-ink)] border-transparent shadow-[var(--shadow-sm)] hover:brightness-110 active:brightness-95',
  secondary:
    'bg-[var(--surface)] text-[var(--ink)] border-[var(--line)] hover:bg-[var(--surface-2)] hover:border-[var(--ink-3)]',
  ghost:
    'bg-transparent text-[var(--ink-2)] border-transparent hover:bg-[var(--surface-2)] hover:text-[var(--ink)]',
  danger:
    'bg-[var(--debit)] text-white border-transparent shadow-[var(--shadow-sm)] hover:brightness-110',
  link: 'bg-transparent border-transparent text-[var(--accent)] underline-offset-2 hover:underline px-0',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-[12.5px] rounded-[var(--radius-sm)] gap-1.5',
  md: 'h-9 px-4 text-[13px] rounded-[var(--radius)] gap-2',
  lg: 'h-11 px-5 text-[14.5px] rounded-[11px] gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner and blocks interaction. Keeps its width, so a posting
   *  toolbar does not jump while the voucher saves. */
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center border font-medium whitespace-nowrap',
        'transition-[background,color,border-color,filter] duration-[var(--dur-fast)]',
        'disabled:cursor-not-allowed disabled:opacity-55',
        VARIANT[variant],
        SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});

function Spinner() {
  return (
    <svg
      className="animate-spin"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'leadingIcon' | 'trailingIcon'> {
  /** Required: an icon-only control is invisible to a screen reader without it. */
  label: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, size = 'md', variant = 'secondary', className, children, ...rest },
  ref,
) {
  const box = size === 'sm' ? 'size-7' : size === 'lg' ? 'size-10' : 'size-8';
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      aria-label={label}
      title={label}
      className={cn('p-0', box, className)}
      {...rest}
    >
      {children}
    </Button>
  );
});

/** Segmented control — the role switch and density toggle in the top bar. */
export interface ButtonGroupOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

export interface ButtonGroupProps<T extends string> {
  options: ButtonGroupOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  'aria-label': string;
}

export function ButtonGroup<T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}: ButtonGroupProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex shrink-0 items-center rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--surface-2)] p-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            title={opt.hint}
            onClick={() => onChange(opt.value)}
            className={cn(
              'cursor-pointer rounded-[6px] border-0 px-2.5 py-1 text-[11.5px] font-medium whitespace-nowrap',
              'transition-colors duration-[var(--dur-fast)]',
              active
                ? 'bg-[var(--surface)] text-[var(--ink)] shadow-[var(--shadow-sm)]'
                : 'bg-transparent text-[var(--ink-3)] hover:text-[var(--ink-2)]',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
